<template>
  <v-container>
    <v-form ref="manualOrderForm">
      <v-card class="mb-4">
        <v-card-title>Company Information</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.company_name"
                label="Company Name *"
                outlined
                dense
                :rules="rules.required"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Basic Information -->
      <v-card class="mb-4">
        <v-card-title>Basic Information</v-card-title>
        <v-card-text>
          <v-row>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.customer_name"
                label="Customer Name *"
                outlined
                dense
                :rules="rules.required"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.customer_email"
                label="Customer Email *"
                type="email"
                outlined
                dense
                :rules="rules.email"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.customer_phone"
                label="Customer Phone *"
                outlined
                dense
                :rules="rules.required"
                placeholder="+36301234567"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.tax_number"
                label="Tax Number"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="manuallyCreated.order_date"
                    label="Order Date *"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    :rules="rules.required"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="manuallyCreated.order_date"
                  @input="dateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="manuallyCreated.user_id"
                :items="users"
                item-value="id"
                item-text="name"
                label="Administrator *"
                outlined
                dense
                :rules="rules.required"
              ></v-select>
            </v-col>

            <v-col cols="12" md="4">
              <v-menu
                v-model="dateMenuDelivery"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="manuallyCreated.meta.delivery_date"
                    label="Delivery date"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="manuallyCreated.meta.delivery_date"
                  @input="dateMenuDelivery = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="manuallyCreated.meta.sales_point_id"
                :items="salesPointCollection"
                item-value="id"
                item-text="name"
                label="Sales Point"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="manuallyCreated.meta.color"
                :items="colorOrder"
                label="Color"
                dense
                outlined
                clearable
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <span class="color-swatch" :style="{ backgroundColor: palette[item] || '#E5E7EB' }"></span>
                    <span class="ml-2 text-capitalize">{{ item }}</span>
                  </div>
                </template>
              
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <span class="color-swatch" :style="{ backgroundColor: palette[item] || '#E5E7EB' }"></span>
                    <span class="ml-2 text-capitalize">{{ item || 'default' }}</span>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Billing Address -->
      <v-card class="mb-4">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Billing Address</span>
          <v-btn text small color="primary" @click="copyShippingToBilling">
            <v-icon small left>mdi-content-copy</v-icon>
            Copy from Shipping
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-combobox
                v-model="manuallyCreated.billing_address.city"
                label="City *"
                :items="billingCities"
                item-text="place_name"
                item-value="place_name"
                clearable
                cache-items
                :return-object="false"
                ref="billingCity"
                :key="hqBillingCityKey"
                @focus="activeField = 'billing_city'"
                @click="activeField = 'billing_city'"
                @keydown.native="activeField = 'billing_city'"
                :rules="rules.required"
                dense
                outlined
                :id="dynamicID"
              ></v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-combobox
                v-model="manuallyCreated.billing_address.postal_code"
                label="Postal Code *"
                :rules="rules.required"
                :id="dynamicID"
                :items="billingPostalItems"
                item-text="postal_code"
                item-value="postal_code"
                clearable
                :return-object="false"
                ref="billingPostal"
                :key="billingPostalKey"
                @focus="shipping_activeField = 'billing_post'"
                @click="shipping_activeField = 'billing_post'"
                @keydown.native="shipping_activeField = 'billing_post'"
                dense
                outlined
              ></v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.billing_address.address"
                label="Address *"
                outlined
                dense
                clearable
                :rules="rules.required"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Shipping Address -->
      <v-card class="mb-4">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Shipping Address</span>
          <v-btn text small color="primary" @click="copyBillingToShipping">
            <v-icon small left>mdi-content-copy</v-icon>
            Copy from Billing
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-combobox
                v-model="manuallyCreated.shipping_address.city"
                label="City"
                :items="shippingCities"
                item-text="place_name"
                item-value="place_name"
                clearable
                cache-items
                :return-object="false"
                ref="shippingCity"
                :key="shippingCityKey"
                @focus="shipping_activeField = 'shipping_city'"
                @click="shipping_activeField = 'shipping_city'"
                @keydown.native="shipping_activeField = 'shipping_city'"
                dense
                outlined
                :id="dynamicID"
              ></v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-combobox
                v-model="manuallyCreated.shipping_address.postal_code"
                label="Postal Code"
                :id="dynamicID"
                :items="shippingPostalItems"
                item-text="postal_code"
                item-value="postal_code"
                clearable
                :return-object="false"
                ref="shippingPostal"
                :key="shippingPostalKey"
                @focus="shipping_activeField = 'shipping_post'"
                @click="shipping_activeField = 'shipping_post'"
                @keydown.native="shipping_activeField = 'shipping_post'"
                dense
                outlined
              ></v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="manuallyCreated.shipping_address.address"
                label="Address"
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Items & Comment -->
      <v-card class="mb-4">
        <v-card-title>Items & Additional Info</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="manuallyCreated.items_text"
                label="Items (free text) *"
                outlined
                rows="4"
                :rules="rules.required"
                placeholder="1 db - KM-MV-4-5801 - Karámelem /305x160cm/ /38-as körszelvény és 40x30-as zártszelvény"
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="manuallyCreated.comment"
                label="Comment"
                outlined
                rows="3"
                placeholder="Additional notes..."
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Action Buttons -->
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="resetForm"> {{ editData.id ? 'Cancel' : 'Reset' }} </v-btn>
        <v-btn color="primary" @click="submitForm" :loading="submitting">
          {{ editData.id ? 'Update Order' : 'Create Order' }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-container>
</template>

<script>
//import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import ApiService from "@/core/services/api.service";
export const ENDPOINT = "sales/Orders/";

export const initialFormData = () => ({
  id: null,
  company_name: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  shipping_address: {
    city: "",
    address: "",
    postal_code: "",
  },
  billing_address: {
    city: "",
    address: "",
    postal_code: "",
  },
  tax_number: "",
  order_date: null,
  total_net: "",
  total_gross: "",
  currency: "",
  paid: 0,
  payment_status: "",
  user_id: null,
  comment: "",
  items_text: "",
  meta:{
    delivery_date: null,
    sales_point_id: null,
    color: 'default',
  }
});

export default {
  name: "ManualOrderForm",
  props:{
    editData: {
      type: Object,
      default: () => ({ id: null, data: null}),
    }
  },
  data() {
    return {
      dateMenu: false,
      dateMenuDelivery: false,
      users: [],
      rules: {
        required: [(v) => !!v || "This field is required"],
        email: [
          (v) => !!v || "Email is required",
          (v) => /.+@.+\..+/.test(v) || "Email must be valid",
        ],
        positiveNumber: [
          (v) => (v !== "" && v !== null) || "This field is required",
          (v) => v >= 0 || "Must be a positive number",
        ],
      },
      manuallyCreated: initialFormData(),
      activeField: null,
      shipping_activeField: null,

      county_list: [],
      billingCities: [],
      billingCityKey: 0,
      billingCityFiltered: false,

      billingPostalItems: [],
      billingPostalKey: 0,
      billingPostalFiltered: false,

      shippingCities: [],
      shippingCityKey: 0,
      shippingCityFiltered: false,

      shippingPostalItems: [],
      shippingPostalKey: 0,
      shippingPostalFiltered: false,
      colorOrder: [
        'default',
        'red','orange','amber','yellow','lime','green','emerald',
        'teal','cyan','sky','blue','indigo','violet','purple','pink'
      ],

      palette: {
        default: '#E5E7EB',
        red: '#EF4444',
        orange: '#F97316',
        amber: '#F59E0B',
        yellow: '#EAB308',
        lime: '#84CC16',
        green: '#22C55E',
        emerald: '#10B981',
        teal: '#14B8A6',
        cyan: '#06B6D4',
        sky: '#0EA5E9',
        blue: '#3B82F6',
        indigo: '#6366F1',
        violet: '#8B5CF6',
        purple: '#A855F7',
        pink: '#EC4899',
      },
    };
  },
  computed: {
    ...mapGetters(["currentUser","salesPointCollection"]),
  },
  methods: {
    ...mapActions([
      "fetchSalesPoint",
    ]),
    copyBillingToShipping() {
      this.manuallyCreated.shipping_address = {
        ...this.manuallyCreated.billing_address,
      };
    },
    copyShippingToBilling() {
      this.manuallyCreated.billing_address = {
        ...this.manuallyCreated.shipping_address,
      };
    },

    resetForm() {
      this.$refs.manualOrderForm.reset();
      this.manuallyCreated = {
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        shipping_address: { city: "", address: "", postal_code: "" },
        billing_address: { city: "", address: "", postal_code: "" },
        tax_number: "",
        order_date: null,
        total_net: "",
        total_gross: "",
        currency: "",
        paid: 0,
        payment_status: "",
        user_id: this.currentUser.id,
        comment: "",
        items_text: "",
        meta: {
          delivery_date: null,
          sales_point_id: null,
          color: 'default',
        },
      };
      if(this.editData.id){
        this.$emit("cancelEdit");
      }
    },

    submitForm() {
      if (this.$refs.manualOrderForm.validate()) {
        // API hívás
        const payload = {
          ...this.manuallyCreated,
          billing_address: {
            billing: { ...this.manuallyCreated.billing_address },
          },
          shipping_address: {
            shipping: { ...this.manuallyCreated.shipping_address },
          },
        };

        if(this.editData.id){
          ApiService.update(ENDPOINT + "update", this.editData.id , payload)
          .then(() => {
            Swal.fire("Success!", "Order Updated", "success");
            this.resetForm();
            this.$emit("saveAndFetch");
          })
          .catch((error) => {
            Swal.fire("Error!", "Something went wrong", "error");
            console.log("status:", error?.response?.status);
            console.log("data:", error?.response?.data); 
            console.log("message:", error?.message);
          });
        }else{
          ApiService.post(ENDPOINT + "manual", payload)
          .then(() => {
            //console.log(response);
            Swal.fire("Success!", "Order created", "success");
            this.resetForm();
            this.$emit("saveAndFetch");
          })
          .catch((error) => {
            Swal.fire("Error!", "Something went wrong", "error");
            console.error(error);
          });
        }
      }else{
        const dom = document.querySelector(".v-text-field__details")?.closest(".v-input");

        if(dom){
          this.$vuetify.goTo(dom, { duration: 350, offset: 90 });
        }
      }
    },

    fetchCounties() {
      return ApiService.get("contact/county_list").then(({ data }) => {
        if (data) {
          this.county_list = data;

          this.billingCities = this.county_list || [];
          this.billingPostalItems = this.county_list || [];

          this.shippingCities = this.county_list || [];
          this.shippingPostalItems = this.county_list || [];
        }
      });
    },
    handleEdit(){
      if (!this.editData?.id) return;
      if (!this.editData?.data) return;

      const src = this.editData.data;

      const b = src?.billing_address?.billing ?? src?.billing_address ?? {};
      const s = src?.shipping_address?.shipping ?? src?.shipping_address ?? {};

      Object.assign(this.manuallyCreated, {
        ...src,
      
        billing_address: {
          city: b.city ?? "",
          address: b.address ?? "",
          postal_code: b.postal_code ?? "",
        },
      
        shipping_address: {
          city: s.city ?? "",
          address: s.address ?? "",
          postal_code: s.postal_code ?? "",
        },
      });
      this.users = [
        { id: this.editData.data.user.id, name: this.editData.data.user.christian_name1 },
      ];
      //console.log(this.manuallyCreated);
    }
  },
  watch: {
    'manuallyCreated.meta.color'(v) {
      if (v == null || v === '') this.manuallyCreated.meta.color = 'default';
    },
    "manuallyCreated.billing_address.city": {
      handler(value) {
        const q = String(value ?? "").trim();

        if (!q) {
          if (!this.billingPostalFiltered) return;
          this.billingPostalFiltered = false;
          this.billingPostalItems = this.county_list || [];
          this.billingPostalKey++;
          return;
        }

        const matches = (this.county_list || []).filter(
          (e) => String(e.place_name ?? "").trim() === q
        );

        if (matches.length === 0) {
          if (!this.billingPostalFiltered) return;
          this.billingPostalFiltered = false;
          this.billingPostalItems = this.county_list || [];
          this.billingPostalKey++;
          return;
        }

        if (matches.length === 1) {
          this.billingPostalFiltered = true;
          this.billingPostalItems = matches;
          this.billingPostalKey++;
          this.manuallyCreated.billing_address.postal_code =
            matches[0].postal_code;
          return;
        }

        if (this.activeField !== "billing_city") return;

        this.billingPostalFiltered = true;
        this.billingPostalItems = matches;
        this.billingPostalKey++;

        const current = this.manuallyCreated.billing_address.postal_code;
        const exists = matches.some((m) => m.place_name === current);
        if (!exists) this.manuallyCreated.billing_address.postal_code = null;

        this.$nextTick(() => {
          const c = this.$refs.billingPostal;
          if (c) {
            c.focus();
            c.isMenuActive = true;
          }
        });
      },
    },
    "manuallyCreated.billing_address.postal_code": {
      handler(value) {
        const q = String(value ?? "").trim();

        if (!q) {
          if (!this.billingCityFiltered) return;
          this.billingCityFiltered = false;
          this.billingCities = this.county_list || [];
          this.billingCityKey++;
          return;
        }

        const matches = (this.county_list || []).filter(
          (e) => String(e.postal_code ?? "").trim() === q
        );

        if (matches.length === 0) {
          if (!this.billingCityFiltered) return;
          this.billingCityFiltered = false;
          this.billingCities = this.county_list || [];
          this.billingCityKey++;
          return;
        }

        if (matches.length === 1) {
          this.billingCityFiltered = true;
          this.billingCities = matches;
          this.billingCityKey++;
          this.manuallyCreated.billing_address.city = matches[0].place_name;
          return;
        }

        if (this.activeField !== "billing_post") return;

        this.billingCityFiltered = true;
        this.billingCities = matches;
        this.billingCityKey++;

        const current = this.manuallyCreated.billing_address.city;
        const exists = matches.some((m) => m.place_name === current);
        if (!exists) this.manuallyCreated.billing_address.city = null;

        this.$nextTick(() => {
          const c = this.$refs.billingCity;
          if (c) {
            c.focus();
            c.isMenuActive = true;
          }
        });
      },
    },
    "manuallyCreated.shipping_address.city": {
      handler(value) {
        const q = String(value ?? "").trim();

        if (!q) {
          if (!this.shippingPostalFiltered) return;
          this.shippingPostalFiltered = false;
          this.shippingPostalItems = this.county_list || [];
          this.shippingPostalKey++;
          return;
        }

        const matches = (this.county_list || []).filter(
          (e) => String(e.place_name ?? "").trim() === q
        );

        if (matches.length === 0) {
          if (!this.shippingPostalFiltered) return;
          this.shippingPostalFiltered = false;
          this.shippingPostalItems = this.county_list || [];
          this.shippingPostalKey++;
          return;
        }

        if (matches.length === 1) {
          this.shippingPostalFiltered = true;
          this.shippingPostalItems = matches;
          this.shippingPostalKey++;
          this.manuallyCreated.shipping_address.postal_code =
            matches[0].postal_code;
          return;
        }

        if (this.shipping_activeField !== "shipping_city") return;

        this.shippingPostalFiltered = true;
        this.shippingPostalItems = matches;
        this.shippingPostalKey++;

        const current = this.manuallyCreated.shipping_address.postal_code;
        const exists = matches.some((m) => m.place_name === current);
        if (!exists) this.manuallyCreated.shipping_address.postal_code = null;

        this.$nextTick(() => {
          const c = this.$refs.shippingPostal;
          if (c) {
            c.focus();
            c.isMenuActive = true;
          }
        });
      },
    },
    "manuallyCreated.shipping_address.postal_code": {
      handler(value) {
        const q = String(value ?? "").trim();

        if (!q) {
          if (!this.shippingCityFiltered) return;
          this.shippingCityFiltered = false;
          this.shippingCities = this.county_list || [];
          this.shippingCityKey++;
          return;
        }

        const matches = (this.county_list || []).filter(
          (e) => String(e.postal_code ?? "").trim() === q
        );

        if (matches.length === 0) {
          if (!this.billingCityFiltered) return;
          this.shippingCityFiltered = false;
          this.shippingCities = this.county_list || [];
          this.shippingCityKey++;
          return;
        }

        if (matches.length === 1) {
          this.shippingCityFiltered = true;
          this.shippingCities = matches;
          this.shippingCityKey++;
          this.manuallyCreated.shipping_address.city = matches[0].place_name;
          return;
        }

        if (this.shipping_activeField !== "shipping_post") return;

        this.shippingCityFiltered = true;
        this.shippingCities = matches;
        this.shippingCityKey++;

        const current = this.manuallyCreated.shipping_address.city;
        const exists = matches.some((m) => m.place_name === current);
        if (!exists) this.manuallyCreated.shipping_address.city = null;

        this.$nextTick(() => {
          const c = this.$refs.shippingCity;
          if (c) {
            c.focus();
            c.isMenuActive = true;
          }
        });
      },
    },
    "editData":{
      handler(){
        this.handleEdit()
      }
    }
  },
  mounted() {
    this.users = [
      { id: this.currentUser.id, name: this.currentUser.christian_name1 },
    ];
    this.manuallyCreated.user_id = this.currentUser.id;
    this.fetchCounties();
    this.handleEdit();
    this.fetchSalesPoint();
  },
};
</script>
<style>
.color-swatch{
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,0.15);
}
</style>
